import * as React from "react"
import { AlertCircle, Calendar, ExternalLink } from "lucide-react"
import { cn } from "../../lib/utils"
import { cva, type VariantProps } from "class-variance-authority"

const bannerWidgetVariants = cva(
  "dsw-relative dsw-w-full dsw-rounded-lg dsw-border dsw-px-4 dsw-py-4 dsw-pt-6 dsw-text-sm [&>svg+div]:dsw-translate-y-[-3px] [&>svg]:dsw-absolute [&>svg]:dsw-left-4 [&>svg]:dsw-top-4 [&>svg]:dsw-text-foreground [&>svg~*]:dsw-pl-7",
  {
    variants: {
      variant: {
        default: "dsw-bg-background dsw-text-foreground",
        destructive:
          "dsw-border-destructive/50 dsw-text-destructive dsw-dark:border-destructive [&>svg]:dsw-text-destructive",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface ContentEntry {
  // Core fields
  title: string
  description: string
  date: string
  link: string
  linkText?: string
  
  // Event-specific fields
  speaker?: string
  time?: string
  location?: string
  price?: string
  duration?: string
  category?: string
  organizer?: string
  capacity?: string
  status?: 'available' | 'sold-out' | 'cancelled'
  
  // Optional content
  image?: string
  details?: string
  extras?: string
}

interface BannerWidgetProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof bannerWidgetVariants> {
  entry: ContentEntry
  showIcon?: boolean
}

const BannerWidget = React.forwardRef<HTMLDivElement, BannerWidgetProps>(
  ({ entry, variant = "default", className, showIcon = true, ...props }, ref) => {
    // Data validation and cleanup
    const hasValidLink = entry.link && entry.link.trim() && entry.link !== '#'
    const defaultLinkText = entry.speaker ? "Register Now" : "Learn More"
    
    return (
      <div 
        ref={ref} 
        role="alert"
        className={cn(bannerWidgetVariants({ variant }), className)} 
        {...props}
      >
        {showIcon && <AlertCircle className="dsw-h-4 dsw-w-4" />}
        <div className="dsw-mb-1 dsw-font-medium dsw-leading-none dsw-tracking-tight dsw-mb-2">
          <div className="dsw-mb-2">
            {entry.title}
          </div>
          <div className="dsw-flex dsw-items-center dsw-gap-1 dsw-text-xs dsw-text-muted-foreground dsw-font-normal">
            <Calendar className="dsw-h-3 dsw-w-3" />
            {entry.date}
            {` • ${entry.time || 'Time TBA'}`}
          </div>
        </div>
        <div className="dsw-text-sm [&_p]:dsw-leading-relaxed dsw-mb-4">
          {entry.description}
          <p className="dsw-mt-2 dsw-font-medium">
            <strong>Speaker:</strong> {entry.speaker || 'Speaker TBA'}
          </p>
          {entry.location && entry.location.trim() && (
            <p className="dsw-mt-1">
              <strong>Location:</strong> {entry.location}
            </p>
          )}
          {entry.price && entry.price.trim() && (
            <p className="dsw-mt-1">
              <strong>Price:</strong> {entry.price}
            </p>
          )}
          {entry.duration && (
            <p className="dsw-mt-1">
              <strong>Duration:</strong> {entry.duration}
            </p>
          )}
          {entry.organizer && (
            <p className="dsw-mt-1">
              <strong>Organizer:</strong> {entry.organizer}
            </p>
          )}
          {entry.capacity && (
            <p className="dsw-mt-1">
              <strong>Capacity:</strong> {entry.capacity}
            </p>
          )}
          {entry.status && entry.status !== 'available' && (
            <p className={cn(
              "dsw-mt-2 dsw-font-medium dsw-px-2 dsw-py-1 dsw-rounded dsw-text-xs dsw-inline-block",
              entry.status === 'sold-out' && "dsw-bg-red-100 dsw-text-red-800",
              entry.status === 'cancelled' && "dsw-bg-gray-100 dsw-text-gray-800"
            )}>
              {entry.status === 'sold-out' ? 'Sold Out' : 'Cancelled'}
            </p>
          )}
        </div>
        
        {/* Action Button - Responsive positioning */}
        <div className="dsw-flex dsw-justify-start sm:dsw-justify-end dsw-mt-4">
          {hasValidLink ? (
            <a 
              href={entry.link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="dsw-inline-flex dsw-items-center dsw-justify-center dsw-whitespace-nowrap dsw-text-sm dsw-font-medium dsw-ring-offset-background dsw-transition-colors dsw-focus-visible:dsw-outline-none dsw-focus-visible:dsw-ring-2 dsw-focus-visible:dsw-ring-ring dsw-focus-visible:dsw-ring-offset-2 dsw-disabled:dsw-pointer-events-none dsw-disabled:dsw-opacity-50 dsw-cursor-pointer dsw-border dsw-border-input dsw-bg-background hover:dsw-bg-accent hover:dsw-text-accent-foreground dsw-h-9 dsw-px-3 dsw-rounded-md"
            >
              <ExternalLink className="dsw-h-3 dsw-w-3 dsw-mr-1" />
              {entry.linkText || defaultLinkText}
            </a>
          ) : (
            <span className="dsw-text-xs dsw-text-muted-foreground dsw-italic">
              Contact organizer for registration
            </span>
          )}
        </div>
      </div>
    )
  }
)
BannerWidget.displayName = "BannerWidget"

export { BannerWidget, bannerWidgetVariants } 